import type { Inputs } from './roiCalculations'

// Commission modeling for residual products (insurance, pest control, etc.).
//
// Reps are typically paid an upfront commission on the customer's first-year
// contract value (ACV) in the month of sale. If the customer cancels inside
// the clawback window, that commission is recovered. Some plans also pay a
// residual commission on every recurring payment, and a backend payment
// (% of first-year ACV) on customers still active when the window closes.
//
// When commissions are modeled here, the margin input means margin BEFORE
// commissions, and gross profit each month is:
//   revenue × margin − upfront − residual − backend + clawbacks

const pct = (value: number | undefined) =>
  Math.min(1, Math.max(0, (Number(value) || 0) / 100))

export const DEFAULT_CLAWBACK_MONTHS = 6

const isResidual = (inputs: Inputs) =>
  (inputs.revenueCadence === 'monthly' ||
    inputs.revenueCadence === 'quarterly') &&
  !!inputs.residualTicket

/** True when commissions are modeled separately from the margin. */
export function commissionsModeled(inputs: Inputs): boolean {
  return (
    isResidual(inputs) &&
    (pct(inputs.commissionPct) > 0 ||
      pct(inputs.residualCommissionPct) > 0 ||
      pct(inputs.backendCommissionPct) > 0)
  )
}

/** Backend commission per surviving customer, paid when the clawback window
 *  closes: a % of the customer's first-year ACV. */
export function backendCommissionPerCustomer(inputs: Inputs): number {
  return pct(inputs.backendCommissionPct) * firstYearAcv(inputs)
}

/** Contract value of one customer's first year: the initial ticket plus the
 *  recurring payments due in the rest of the year. */
export function firstYearAcv(inputs: Inputs): number {
  const ticket = Math.max(0, inputs.ticket || 0)
  const residual = Math.max(0, inputs.residualTicket || 0)
  if (inputs.revenueCadence === 'monthly') return ticket + residual * 11
  if (inputs.revenueCadence === 'quarterly') return ticket + residual * 3
  return ticket
}

/** Upfront commission paid per sale, in the month of sale. */
export function upfrontCommissionPerSale(inputs: Inputs): number {
  return pct(inputs.commissionPct) * firstYearAcv(inputs)
}

export function clawbackMonths(inputs: Inputs): number {
  const value = inputs.clawbackMonths
  return value === undefined || value === null || (value as unknown) === ''
    ? DEFAULT_CLAWBACK_MONTHS
    : Math.max(0, Math.round(Number(value)))
}

export interface CommissionSchedule {
  upfront: number[]
  residual: number[]
  clawbacks: number[]
  /** Backend paid when each month's clawback window closes, on survivors. */
  backend: number[]
  /** Net commission cost each month (upfront + residual + backend − clawbacks). */
  net: number[]
}

/**
 * Commission cash flow month by month.
 * @param sales realized (post pull-through) new customers each month
 * @param residualRevenue recurring revenue collected each month
 */
export function commissionSchedule(
  inputs: Inputs,
  sales: number[],
  residualRevenue: number[],
): CommissionSchedule {
  const months = sales.length
  const zeros = () => Array(months).fill(0) as number[]
  if (!commissionsModeled(inputs)) {
    return {
      upfront: zeros(),
      residual: zeros(),
      clawbacks: zeros(),
      backend: zeros(),
      net: zeros(),
    }
  }

  const perSale = upfrontCommissionPerSale(inputs)
  const window = clawbackMonths(inputs)
  const upfront = sales.map((n) => n * perSale)
  const residual = residualRevenue.map(
    (r) => (r || 0) * pct(inputs.residualCommissionPct),
  )

  // Customers who cancel within the window have their commission recovered
  // in the month they cancel. Cancellations follow the same attrition curve
  // as the residual revenue.
  const clawbacks = zeros()
  if (perSale > 0 && window > 0) {
    if (inputs.revenueCadence === 'monthly') {
      const attrition = pct(inputs.monthlyAttrition || 2)
      for (let s = 0; s < months; s++) {
        // The first recurring payment (month s+1) is collected from every
        // customer; losses start before the second one.
        for (let d = 2; d <= window && s + d < months; d++) {
          const lost = sales[s] * Math.pow(1 - attrition, d - 2) * attrition
          clawbacks[s + d] += lost * perSale
        }
      }
    } else {
      const attrition = pct(inputs.quarterlyAttrition || 5)
      if (window >= 3) {
        for (let s = 0; s + 3 < months; s++) {
          clawbacks[s + 3] += sales[s] * attrition * perSale
        }
      }
    }
  }

  // Backend: once the clawback window closes, reps are paid a % of first-year
  // ACV on every customer still on the books, in that month.
  const backend = zeros()
  const backendPer = backendCommissionPerCustomer(inputs)
  if (backendPer > 0) {
    const closes = Math.max(0, window)
    let survival = 1
    if (inputs.revenueCadence === 'monthly') {
      // Same curve as the clawbacks: losses start before the 2nd payment.
      const attrition = pct(inputs.monthlyAttrition || 2)
      survival = Math.pow(1 - attrition, Math.max(0, closes - 1))
    } else if (closes >= 3) {
      survival = 1 - pct(inputs.quarterlyAttrition || 5)
    }
    for (let s = 0; s + closes < months; s++) {
      backend[s + closes] += sales[s] * survival * backendPer
    }
  }

  const net = upfront.map(
    (u, m) => u + residual[m] + backend[m] - clawbacks[m],
  )
  return { upfront, residual, clawbacks, backend, net }
}

// ─── Manager / market-owner view ────────────────────────────────────────────
//
// Instead of company gross profit, the model can show what a manager earns in
// overrides on every account their team writes:
//   • Upfront override: a % of first-year ACV, paid in the month of sale.
//   • Backend override: a % of first-year ACV on accounts still in good
//     standing once the clawback window closes, MINUS the upfront override
//     clawed back on accounts that cancelled inside the window.
// The backend is paid either on a rolling basis (each month's accounts are
// settled when their window closes) or as a once-a-year bonus in a chosen
// month, covering every account whose window has closed since the last one.

/** True when the calculator is showing the manager's override income. */
export function managerView(inputs: Inputs): boolean {
  return isResidual(inputs) && inputs.modelPerspective === 'manager'
}

export function upfrontOverridePerSale(inputs: Inputs): number {
  return pct(inputs.overrideUpfrontPct) * firstYearAcv(inputs)
}

export function backendOverridePerAccount(inputs: Inputs): number {
  return pct(inputs.overrideBackendPct) * firstYearAcv(inputs)
}

/** Month of the year (1–12) the backend bonus is paid, or 0 for rolling. */
export function overrideBonusMonth(inputs: Inputs): number {
  const value = Math.round(Number(inputs.overrideBonusMonth) || 0)
  return value >= 1 && value <= 12 ? value : 0
}

/** Share of a month's accounts still in good standing when the window closes. */
function survivalAtWindowClose(inputs: Inputs, window: number): number {
  if (inputs.revenueCadence === 'monthly') {
    const attrition = pct(inputs.monthlyAttrition || 2)
    // Losses start before the 2nd payment, same curve as the clawbacks.
    return Math.pow(1 - attrition, Math.max(0, window - 1))
  }
  return window >= 3 ? 1 - pct(inputs.quarterlyAttrition || 5) : 1
}

export interface OverrideSchedule {
  upfront: number[]
  /** Backend override earned on surviving accounts, by payout month. */
  backend: number[]
  /** Upfront overrides clawed back on early cancels, by payout month. */
  clawbacks: number[]
  /** Manager's override income each month. */
  net: number[]
}

/**
 * Manager override income month by month.
 * @param sales realized (post pull-through) new accounts each month
 */
export function overrideSchedule(
  inputs: Inputs,
  sales: number[],
): OverrideSchedule {
  const months = sales.length
  const zeros = () => Array(months).fill(0) as number[]
  const upfrontPer = upfrontOverridePerSale(inputs)
  const backendPer = backendOverridePerAccount(inputs)
  const window = clawbackMonths(inputs)
  const survival = survivalAtWindowClose(inputs, window)
  const bonusMonth = overrideBonusMonth(inputs)

  const upfront = sales.map((n) => n * upfrontPer)
  const backend = zeros()
  const clawbacks = zeros()

  if (!bonusMonth) {
    // Rolling: each month's accounts settle when their window closes.
    for (let s = 0; s < months; s++) {
      if (!sales[s]) continue
      const target = s + window
      if (target >= months) continue
      backend[target] += sales[s] * survival * backendPer
      clawbacks[target] += sales[s] * (1 - survival) * upfrontPer
    }
  } else {
    // Bonus: one bonus payout, in the first chosen bonus month on or after
    // the first accounts' window closes. Accounts whose window has closed by
    // then are settled in that bonus. Accounts still inside their window
    // settle on a rolling basis as each window closes, so there's no second
    // bonus in year two or three. Model month m is "Month m + 1".
    const firstSale = sales.findIndex((n) => n > 0)
    let payout = months
    if (firstSale >= 0) {
      for (let m = firstSale + window; m < months; m++) {
        if ((m + 1 - bonusMonth) % 12 === 0) {
          payout = m
          break
        }
      }
    }
    for (let s = 0; s < months; s++) {
      if (!sales[s]) continue
      const closes = s + window
      const target = closes <= payout ? payout : closes
      if (target >= months) continue
      backend[target] += sales[s] * survival * backendPer
      clawbacks[target] += sales[s] * (1 - survival) * upfrontPer
    }
  }

  const net = upfront.map((u, m) => u + backend[m] - clawbacks[m])
  return { upfront, backend, clawbacks, net }
}

/**
 * Gross profit each month for the active perspective: company margin after
 * commissions, or the manager's override income.
 */
export function profitByMonth(
  inputs: Inputs,
  sales: number[],
  residualRevenue: number[],
  grossRevenue: number[],
  gpm: number,
): { profit: number[]; commissions: CommissionSchedule; overrides: OverrideSchedule | null } {
  const commissions = commissionSchedule(inputs, sales, residualRevenue)
  if (managerView(inputs)) {
    const overrides = overrideSchedule(inputs, sales)
    return { profit: overrides.net, commissions, overrides }
  }
  return {
    profit: grossRevenue.map((x, m) => x * gpm - commissions.net[m]),
    commissions,
    overrides: null,
  }
}
